import { useState, useRef, useCallback, SyntheticEvent, RefObject, useEffect } from 'react'
import { registerLocale, setDefaultLocale } from 'react-datepicker'
import { addDays } from 'date-fns'
import { enGB, enUS, es } from 'date-fns/locale'
import { Icon, TextField } from '@alto-avios/alto-ui'
import { useOnClickOutside, useMediaQuery } from 'usehooks-ts'
import MobileModal from '../MobileModal'
import Calendar from './Calendar'
import { deviceSizes } from '../../theme/deviceSizes'
import styles from './DatePicker.module.scss'
import { DatePickerProps } from './DatePicker.types'

const enGBLocaleCode = 'en-GB'
const enUSLocaleCode = 'en-US'
const esESLocaleCode = 'es-ES'

registerLocale(enGBLocaleCode, enGB)
registerLocale(enUSLocaleCode, enUS)
registerLocale(esESLocaleCode, es)
setDefaultLocale(enGBLocaleCode)

const DEFAULT_LOCALE_CODE = enGBLocaleCode

const getLocaleObjectByLocaleCode = (localeCode: string) => {
  if (localeCode === enUSLocaleCode) {
    return enUS
  }

  if (localeCode === esESLocaleCode) {
    return es
  }

  return enGB
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function DatePicker({
  isRangePicker,
  startLabel = 'Start',
  endLabel = 'End',
  minDate = new Date(),
  maxDate = addDays(new Date(), 365),
  values = {
    startDateTime: null,
    endDateTime: null,
  },
  onChange = () => null,
  singleDay = false,
  allowSameDay = false,
  maxEndDate,
  locale = DEFAULT_LOCALE_CODE,
  endDateSelectable = false,
  errorStartDate,
  errorEndDate,
  disabled,
  onRangeCompleteForwardTab,
  controlRequest,
  ...calendarProps
}: DatePickerProps) {
  const [showCalendar, setShowCalendar] = useState(false)
  const [showEndCalendar, setShowEndCalendar] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const isTabletOrSmaller = useMediaQuery(`(max-width: ${deviceSizes.medium})`)

  const datePickerRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const startDateWrapperRef = useRef<HTMLDivElement>(null)
  const endDateWrapperRef = useRef<HTMLDivElement>(null)
  const lastOpenedFieldRef = useRef<'start' | 'end'>('start')
  const shouldForwardNextTabFromStartRef = useRef(false)
  const shouldOpenStartOnNextFocusRef = useRef(false)
  const shouldOpenEndOnNextFocusRef = useRef(false)
  const isMovingFocusIntoCalendarRef = useRef(false)
  const localeObject = getLocaleObjectByLocaleCode(locale)
  const calendarTitle = isRangePicker ? `${startLabel} - ${endLabel}` : startLabel

  const focusOpenedFieldInput = useCallback(() => {
    const targetRef =
      lastOpenedFieldRef.current === 'end' ? endDateWrapperRef.current : startDateWrapperRef.current
    targetRef?.querySelector('input')?.focus()
  }, [])

  const closeCalendarHandler = useCallback(() => {
    if (!isTabletOrSmaller && showCalendar) {
      setShowCalendar(false)
      setShowEndCalendar(false)
    }
  }, [isTabletOrSmaller, showCalendar, setShowCalendar, setShowEndCalendar])

  useOnClickOutside(datePickerRef as RefObject<HTMLInputElement>, closeCalendarHandler)

  const closeCalendarWithoutRestoringFocus = useCallback(() => {
    setShowCalendar(false)
    setShowEndCalendar(false)
  }, [])

  // After opening, move focus into the calendar grid so Arrow keys work immediately
  const focusCalendar = useCallback((preventScroll = false) => {
    isMovingFocusIntoCalendarRef.current = true
    const moveFocus = (attempt = 0) => {
      const calendarElement = calendarRef.current
      if (!calendarElement) {
        isMovingFocusIntoCalendarRef.current = false
        return
      }
      const firstFocusableDate =
        calendarElement.querySelector<HTMLElement>(
          '.react-datepicker__day[tabindex="0"]:not(.react-datepicker__day--disabled)'
        ) ??
        calendarElement.querySelector<HTMLElement>(
          '.react-datepicker__day--keyboard-selected:not(.react-datepicker__day--disabled)'
        ) ??
        calendarElement.querySelector<HTMLElement>(
          '.react-datepicker__day--selected:not(.react-datepicker__day--disabled)'
        ) ??
        calendarElement.querySelector<HTMLElement>(
          '.react-datepicker__day[role="option"]:not(.react-datepicker__day--disabled)'
        )

      if (firstFocusableDate) {
        firstFocusableDate.focus({ preventScroll })
        isMovingFocusIntoCalendarRef.current = false
        return
      }

      // The grid may mount asynchronously; retry briefly before giving up.
      if (attempt < 4) {
        setTimeout(() => moveFocus(attempt + 1), 50)
        return
      }

      calendarElement.focus({ preventScroll })
      isMovingFocusIntoCalendarRef.current = false
    }

    setTimeout(() => moveFocus(), 50)
  }, [])

  const onChangeCalendar = (dates: Date | [Date, Date], event: SyntheticEvent<never>) => {
    if (!isRangePicker) {
      onChange(dates, event)
      setShowCalendar(false)
      setTimeout(() => {
        focusOpenedFieldInput()
      }, 0)
      return
    }

    if (!dates) {
      return
    }

    if (endDateSelectable && !Array.isArray(dates) && !allowSameDay) {
      onChange([null, dates], event)
      setShowCalendar(false)
      shouldForwardNextTabFromStartRef.current = false
      setTimeout(() => {
        focusOpenedFieldInput()
      }, 0)
      return
    }

    const [start, end] = singleDay ? [dates as Date, undefined] : (dates as [Date, Date])

    if (!allowSameDay && end?.getTime() === start.getTime()) {
      end.setDate(start.getDate() + 1)
    }

    onChange([start, end ?? null], event)

    const completedRangeFromStart =
      !singleDay &&
      end != null &&
      lastOpenedFieldRef.current === 'start' &&
      typeof onRangeCompleteForwardTab === 'function'
    shouldForwardNextTabFromStartRef.current = completedRangeFromStart

    if (singleDay || end != null) {
      setShowCalendar(false)
      setTimeout(() => {
        focusOpenedFieldInput()
      }, 0)
    }
  }

  const selectingEndDate =
    !singleDay &&
    isRangePicker &&
    endDateSelectable &&
    showEndCalendar &&
    values.startDateTime !== undefined

  const openStartField = (moveFocusToCalendar: boolean) => {
    lastOpenedFieldRef.current = 'start'
    setShowCalendar(true)
    setShowEndCalendar(false)
    if (moveFocusToCalendar) {
      focusCalendar()
    }
  }
  const openEndField = (moveFocusToCalendar: boolean) => {
    if (singleDay) {
      return
    }
    lastOpenedFieldRef.current = 'end'
    shouldForwardNextTabFromStartRef.current = false
    setShowCalendar(true)
    setShowEndCalendar(endDateSelectable && !!values.startDateTime)
    if (moveFocusToCalendar) {
      focusCalendar()
    }
  }

  useEffect(() => {
    if (!controlRequest) {
      return
    }

    if (controlRequest.action === 'open') {
      lastOpenedFieldRef.current = 'start'
      setShowCalendar(true)
      setShowEndCalendar(false)
      focusCalendar(true)
      return
    }

    if (controlRequest.action === 'close') {
      setShowCalendar(false)
      setShowEndCalendar(false)
    }
  }, [controlRequest, focusCalendar])

  let calendarMinDate = minDate
  let calendarMaxDate = maxDate
  if (isRangePicker && !singleDay && selectingEndDate && !!values.startDateTime) {
    calendarMinDate = addDays(values.startDateTime, 1)
  }
  if (isRangePicker && !singleDay && maxEndDate && (selectingEndDate || !values.endDateTime)) {
    calendarMaxDate = maxEndDate
  }

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    const element = event.target as HTMLElement
    if (element.tagName === 'INPUT') {
      return
    }
    if (startDateWrapperRef.current?.contains(element)) {
      openStartField(true)
    }
    if (endDateWrapperRef.current?.contains(element)) {
      openEndField(true)
    }
  }

  // Close calendar and restore focus to the field that opened it
  const closeCalendarAndRestoreFieldFocus = useCallback(() => {
    shouldOpenStartOnNextFocusRef.current = false
    shouldOpenEndOnNextFocusRef.current = false
    setShowCalendar(false)
    setShowEndCalendar(false)
    setTimeout(() => {
      focusOpenedFieldInput()
    }, 0)
  }, [focusOpenedFieldInput])

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div className={styles.equallySpacedDiv} ref={datePickerRef} onClick={handleClick}>
      <div
        className={styles.placeContentEnd}
        ref={startDateWrapperRef}
        onMouseDownCapture={() => {
          shouldOpenStartOnNextFocusRef.current = true
        }}
      >
        <TextField
          name={startLabel}
          label={startLabel}
          isRequired
          isDisabled={disabled}
          value={values.startDateTime?.toLocaleDateString(locale) || ''}
          onFocus={() => {
            if (!shouldOpenStartOnNextFocusRef.current) {
              return
            }
            shouldOpenStartOnNextFocusRef.current = false
            openStartField(true)
          }}
          onBlur={(event) => {
            if (isMovingFocusIntoCalendarRef.current) {
              return
            }
            const nextElement = event.relatedTarget as HTMLElement | null
            const isMovingInsideDatePicker =
              !!nextElement && datePickerRef.current?.contains(nextElement)

            if (!isMovingInsideDatePicker) {
              closeCalendarWithoutRestoringFocus()
            }

            if (!shouldForwardNextTabFromStartRef.current || !onRangeCompleteForwardTab) {
              return
            }

            const isMovingToEndField =
              !!nextElement && endDateWrapperRef.current?.contains(nextElement)

            if (isMovingToEndField || !nextElement) {
              shouldForwardNextTabFromStartRef.current = false
              setTimeout(() => {
                onRangeCompleteForwardTab()
              }, 0)
            }
          }}
          onKeyDown={(event) => {
            // After completing a range from the start field, Tab should skip to the
            // next logical control (e.g. travellers in Hotels) via callback.
            if (
              event.key === 'Tab' &&
              !event.shiftKey &&
              shouldForwardNextTabFromStartRef.current &&
              onRangeCompleteForwardTab
            ) {
              event.preventDefault()
              shouldForwardNextTabFromStartRef.current = false
              onRangeCompleteForwardTab()
              return
            }
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault()
              openStartField(true)
            }
          }}
          errorMessage={errorStartDate}
          isInvalid={!!errorStartDate}
        >
          {isHydrated && (
            <TextField.EndIcon>
              <Icon
                iconName="calendar"
                color={showCalendar ? 'accentPrimary' : undefined}
                isDecorative
              />
            </TextField.EndIcon>
          )}
        </TextField>
      </div>

      {showCalendar && (
        <div
          className={styles.calendarWrapper}
          role={isTabletOrSmaller ? undefined : 'dialog'}
          aria-label={isTabletOrSmaller ? undefined : calendarTitle}
        >
          <MobileModal
            id="datePickerRange"
            title={calendarTitle}
            isModalVisible={showCalendar && isTabletOrSmaller}
            handleModalClose={closeCalendarAndRestoreFieldFocus}
          >
            <Calendar
              ref={calendarRef}
              tabIndex={-1}
              monthsShown={isRangePicker && !singleDay ? 2 : 1}
              selected={values.startDateTime}
              onChange={onChangeCalendar}
              minDate={calendarMinDate}
              maxDate={calendarMaxDate}
              selectsRange={isRangePicker && !singleDay && !selectingEndDate}
              selectsEnd={selectingEndDate}
              startDate={values.startDateTime}
              endDate={values.endDateTime}
              locale={localeObject}
              {...calendarProps}
            />
          </MobileModal>
        </div>
      )}

      {isRangePicker ? (
        <div
          ref={endDateWrapperRef}
          onMouseDownCapture={() => {
            shouldOpenEndOnNextFocusRef.current = true
          }}
        >
          <TextField
            name={endLabel}
            label={endLabel}
            isRequired
            value={values.endDateTime?.toLocaleDateString(locale) || ''}
            onFocus={() => {
              if (!shouldOpenEndOnNextFocusRef.current) {
                return
              }
              shouldOpenEndOnNextFocusRef.current = false
              openEndField(true)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                event.preventDefault()
                openEndField(true)
              }
            }}
            onBlur={(event) => {
              if (isMovingFocusIntoCalendarRef.current) {
                return
              }
              const nextElement = event.relatedTarget as HTMLElement | null
              const isMovingInsideDatePicker =
                !!nextElement && datePickerRef.current?.contains(nextElement)

              if (!isMovingInsideDatePicker) {
                closeCalendarWithoutRestoringFocus()
              }
            }}
            isDisabled={singleDay}
            errorMessage={errorEndDate}
            isInvalid={!!errorEndDate}
          >
            {isHydrated && (
              <TextField.EndIcon>
                <Icon
                  iconName="calendar"
                  color={showCalendar || showEndCalendar ? 'accentPrimary' : undefined}
                  isDecorative
                />
              </TextField.EndIcon>
            )}
          </TextField>
        </div>
      ) : null}
    </div>
  )
}
